import React, { useState, useEffect, useRef, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, DollarSign, Briefcase, Clock, Bookmark, ExternalLink, FileText, TrendingUp, Zap } from 'lucide-react';
import { supabase } from './supabase';
import { trackFunnel } from './track';
import { isFreshSignup } from './funnel';
import AuthModal from './AuthModal';
import ApplicationsTab from './ApplicationsTab';
import GrowthTab from './GrowthTab';
import './App.css';
import { unescapeMarkdown } from './unescapeMarkdown';
import FeedPage from './feed/FeedPage';
import { isFeedV2Enabled } from './feed/flag';
import ValueHero from './onboarding/ValueHero';
import { ONBOARDING_ENABLED } from './onboarding/constants';
import { shouldAutoOpen, markSeen } from './onboarding/trigger';
import { hasStoredSession } from './onboarding/storedSession';

// Loaded on demand so the wizard and its demo never weigh on the first paint.
const OnboardingWizard = React.lazy(() => import('./onboarding/OnboardingWizard'));

const API_URL = 'https://elevate-careers-api.fly.dev';
const EXTENSION_URL = 'https://chromewebstore.google.com/detail/simplyapply-%E2%80%94-ai-job-auto/ocdeebjeffdjmfgmclnlphkhfdcdpdkf';

function slugify(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function App() {
  const navigate = useNavigate();
  // Cached the same way selectedJob already was — otherwise every fresh
  // mount (navigating to a company page and back, browser back/forward)
  // blanks the list and shows a loading spinner even though the results
  // probably haven't changed since last visit.
  const [jobs, setJobs] = useState(() => {
    try { const s = sessionStorage.getItem('sa_jobs'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [selectedJob, setSelectedJob] = useState(() => {
    try { const s = sessionStorage.getItem('sa_selectedJob'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  // Feed v2 flag. Hook position: top of the body so the effects below can read it.
  const feedV2 = React.useMemo(() => isFeedV2Enabled(window.location.search, window.localStorage), []);
  // Growth-tab skill/title click while v2 is on: FeedPage applies it as its keyword.
  const [feedQuery, setFeedQuery] = useState({ q: '', n: 0 });
  const selectJob = (job) => {
    setSelectedJob(job);
    try { sessionStorage.setItem('sa_selectedJob', JSON.stringify(job)); } catch {}
    // The list response only carries description_excerpt (hard-truncated to
    // 500 chars, cutting off mid-sentence) — fetch the full description_md
    // from job_version for the detail view instead of showing a dead-end
    // "..." with no way to read the rest.
    if (job?.id && !job.description_md) {
      fetch(`${API_URL}/jobs/${job.id}`)
        .then(r => r.ok ? r.json() : null)
        .then(full => {
          // '' (not undefined) marks "fetched, none available" so the feed's
          // detail pane can tell it apart from "still loading".
          const md = full?.description_md || '';
          setSelectedJob(prev => {
            if (!prev || prev.id !== job.id) return prev;
            const updated = { ...prev, description_md: md };
            try { sessionStorage.setItem('sa_selectedJob', JSON.stringify(updated)); } catch {}
            return updated;
          });
        })
        .catch(() => {
          setSelectedJob(prev => {
            if (!prev || prev.id !== job.id) return prev;
            const updated = { ...prev, description_md: '' };
            try { sessionStorage.setItem('sa_selectedJob', JSON.stringify(updated)); } catch {}
            return updated;
          });
        });
    }
  };
  // Only true while there's no cached list to show yet — a background
  // refetch on top of cached results shouldn't blank the page.
  const [loading, setLoading] = useState(jobs.length === 0);
  const [filters, setFilters] = useState({
    keyword: '', remote: false, location: '', employmentType: '', datePosted: '', provider: '',
  });
  const [totalCount, setTotalCount] = useState(() => {
    try { return parseInt(sessionStorage.getItem('sa_totalCount'), 10) || 0; } catch { return 0; }
  });
  const [session, setSession] = useState(null);
  // supabase.auth.getSession() resolves asynchronously, so `session` starts
  // as null even for a signed-in user for a brief moment. Without this flag,
  // the fetch effect below fired immediately on that still-unresolved null,
  // hit the unauthenticated (non-personalized) /jobs endpoint, and overwrote
  // the cached personalized list — invisible before caching was added (both
  // states were hidden behind the loading spinner), but visible as a flash
  // of the wrong list now that a cached list is shown immediately.
  const [sessionChecked, setSessionChecked] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('signin');
  const storedSessionAtLoad = React.useMemo(() => hasStoredSession(), []);
  const [showWizard, setShowWizard] = useState(false);
  const wizardCheckedFor = useRef(null);
  // Honor a #jobs / #applications / #growth link, e.g. the phone app's "Finish on web" button.
  const [activeTab, setActiveTab] = useState(() => {
    const h = (window.location.hash || '').replace('#', '');
    return ['jobs', 'applications', 'growth'].includes(h) ? h : 'jobs';
  });
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [seedingJob, setSeedingJob] = useState(null); // job id being seeded
  const [seedError, setSeedError] = useState('');
  const [pendingApply, setPendingApply] = useState(null); // { id, job_url, job_title, company }
  const [page, setPage] = useState(0);
  const [showApplied, setShowApplied] = useState(false);
  const [appliedCount, setAppliedCount] = useState(0);
  const [dismissMenuJobId, setDismissMenuJobId] = useState(null);
  // Screen coordinates for the portal-rendered dismiss menu below — it's
  // rendered into document.body via createPortal so it isn't clipped by the
  // job list's `overflow-y: auto`, which was cutting the menu off before.
  const [dismissMenuPos, setDismissMenuPos] = useState(null);
  // Marks jobs applied-to in this session so the card/button update the
  // instant Apply is clicked, without waiting for the next server refetch
  // (which excludes applied jobs, but only once it re-runs).
  const [appliedJobIds, setAppliedJobIds] = useState(() => {
    try { const s = sessionStorage.getItem('sa_appliedJobIds'); return s ? new Set(JSON.parse(s)) : new Set(); } catch { return new Set(); }
  });
  const markApplied = (jobId) => {
    setAppliedJobIds(prev => {
      const next = new Set(prev);
      next.add(jobId);
      try { sessionStorage.setItem('sa_appliedJobIds', JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const fetchSeq = useRef(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setSessionChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setSessionChecked(true);
      // Sign-ups that return through a redirect (Google, or the email confirmation link)
      // never reach AuthModal's onSuccess, so detect them here, once per browser.
      if (event === 'SIGNED_IN' && session?.user) {
        try {
          const pending = localStorage.getItem('sa_signup_pending') === '1';
          const google = session.user.app_metadata?.provider === 'google';
          if ((pending || google) && isFreshSignup(session.user) && !localStorage.getItem('sa_signup_tracked')) {
            localStorage.setItem('sa_signup_tracked', '1');
            localStorage.removeItem('sa_signup_pending');
            trackFunnel('signup_complete', { method: google ? 'google' : 'email_confirm' });
          }
        } catch { /* analytics and onboarding hint only */ }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Open the setup wizard once per user per browser, after any sign-in path, when the
  // account has no saved profile. Reopen any time from the header.
  useEffect(() => {
    const uid = session?.user?.id;
    const token = session?.access_token;
    if (!uid || !token || wizardCheckedFor.current === uid) return;
    wizardCheckedFor.current = uid;
    shouldAutoOpen({ uid, token }).then((open) => {
      if (open) { markSeen(uid); setShowWizard(true); }
    });
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // detect.js runs in the extension's isolated world, so its window flag is invisible here.
    // Newer builds also write a data attribute on <html>, which is shared with the page.
    const ver = document.documentElement.getAttribute('data-simplyapply-ext');
    const installed = !!window.__simplyApplyInstalled || ver !== null;
    setExtensionInstalled(installed);
    if (ver !== null) trackFunnel('extension_install_seen', { v: ver });
  }, []);

  useEffect(() => {
    if (dismissMenuJobId === null) return;
    const closeMenu = () => setDismissMenuJobId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [dismissMenuJobId]);

  useEffect(() => {
    if (feedV2) return; // v2: the feed owns data loading; don't hit the old /jobs endpoints
    if (!sessionChecked) return; // wait for the real session before fetching
    setPage(0);
    fetchJobs(0);
  }, [filters, session, sessionChecked]);

  const fetchJobs = async (pageNum = page, overrideFilters = {}) => {
    // Guard against out-of-order responses: if session/filters change quickly
    // (e.g. session hydrating from null -> real on page load), an earlier
    // request can resolve after a later one and clobber it with stale/smaller
    // results. Only the response from the most-recently-issued call wins.
    const mySeq = ++fetchSeq.current;
    // Only show the blocking spinner when there's nothing cached to display —
    // a background refresh on top of an already-visible list shouldn't blank it.
    if (jobs.length === 0) setLoading(true);
    try {
      // overrideFilters lets a caller (e.g. clicking a skill/title in the
      // Growth tab) pass the search term directly instead of going through
      // setFilters + relying on this closure's `filters` — since setFilters
      // is async, a fetchJobs call made right after it in the same handler
      // would otherwise still see the stale pre-update value.
      const f = { ...filters, ...overrideFilters };
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
        ...(f.keyword && { keyword: f.keyword }),
        ...(f.remote && { remote: 'true' }),
        ...(f.location && { location: f.location }),
        ...(f.employmentType && { employment_type: f.employmentType }),
        ...(f.datePosted && { days: f.datePosted }),
        ...(f.provider && { provider: f.provider }),
      });

      // Use personalized feed when signed in and no explicit keyword search
      const usePersonalized = session && !f.keyword && !f.location && !f.remote && !f.provider;
      if (usePersonalized) params.set('show_applied', showApplied ? 'true' : 'false');
      const url = usePersonalized
        ? `${API_URL}/jobs/personalized?${params}`
        : `${API_URL}/jobs?${params}`;
      // Always send the token when signed in, even on the /jobs fallback
      // path (active search/location/remote filter) — that endpoint now
      // applies the same preference-based exclusions (dismissed jobs,
      // excluded companies/titles/locations, non-US filter) when a valid
      // token is present. Previously only /jobs/personalized ever got a
      // token, so a dismissed or non-US job could reappear the instant any
      // filter box was used.
      const headers = session
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};

      const response = await fetch(url, { headers });
      const data = await response.json();
      if (mySeq !== fetchSeq.current) return; // a newer request superseded this one
      const freshJobs = data.jobs || [];
      setJobs(freshJobs);
      setTotalCount(data.count || 0);
      try {
        sessionStorage.setItem('sa_jobs', JSON.stringify(freshJobs));
        sessionStorage.setItem('sa_totalCount', String(data.count || 0));
      } catch {}
      if (data.appliedCount !== undefined) setAppliedCount(data.appliedCount);
      // Only auto-select the first job when nothing is already selected —
      // otherwise a background refresh would keep snapping the detail pane
      // back to job #1 out from under whatever the user had picked.
      if (!selectedJob && freshJobs.length > 0) {
        selectJob(freshJobs[0]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      if (mySeq === fetchSeq.current) setLoading(false);
    }
  };

  // Re-fetch when showApplied toggle changes
  useEffect(() => { if (!feedV2 && session) { setPage(0); fetchJobs(0); } }, [showApplied]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  // Jump to the Jobs tab pre-searched for a term — used by clickable
  // skills/titles in the Growth tab (skill gap path, AI-era opportunities).
  const searchJobs = (keyword) => {
    if (feedV2) {
      setActiveTab('jobs');
      setFeedQuery(prev => ({ q: keyword, n: prev.n + 1 }));
      return;
    }
    setActiveTab('jobs');
    setFilters(prev => ({ ...prev, keyword }));
    setPage(0);
    fetchJobs(0, { keyword });
  };

  // action: 'card' (this session only) | 'company' | 'title' (persisted to prefs)
  const removeJobsFromCache = (predicate) => {
    setJobs(prev => {
      const next = prev.filter(j => !predicate(j));
      try { sessionStorage.setItem('sa_jobs', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const dismissJob = async (job, action) => {
    setDismissMenuJobId(null);
    if (action === 'card') {
      removeJobsFromCache(j => j.id === job.id);
      // Persist server-side when signed in — otherwise this was purely a
      // sessionStorage edit and silently came back on the next fetch (new
      // tab, browser restart, any filter change re-triggering fetchJobs).
      if (session) {
        fetch(`${API_URL}/api/preferences/dismiss-job`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ jobId: job.id }),
        }).catch(e => console.error('Failed to persist card dismissal', e));
      }
      return;
    }
    if (!session) return;
    try {
      const current = await fetch(`${API_URL}/api/preferences`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      }).then(r => r.json());

      const jobLocations = [...(job.cities || []), ...(job.countries || [])];
      const patch = action === 'company'
        ? { excluded_companies: [...new Set([...(current.excluded_companies || []), job.company_name])] }
        : action === 'title'
        ? { excluded_titles: [...new Set([...(current.excluded_titles || []), job.title])] }
        : { excluded_locations: [...new Set([...(current.excluded_locations || []), ...jobLocations])] };

      await fetch(`${API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ ...current, ...patch }),
      });

      removeJobsFromCache(j => {
        if (action === 'company') return j.company_name === job.company_name;
        if (action === 'title') return j.title === job.title;
        const jLocs = [...(j.cities || []), ...(j.countries || [])];
        return jLocs.some(l => jobLocations.includes(l));
      });
    } catch (e) {
      console.error('Failed to dismiss job', e);
    }
  };

  const formatSalary = (job) => {
    if (job.salary_min && job.salary_max) {
      return `$${(job.salary_min / 1000).toFixed(0)}K - $${(job.salary_max / 1000).toFixed(0)}K/yr`;
    }
    return null;
  };

  const getCompanyLogo = (companyName, companyDomain) => {
    if (companyDomain) return `https://logo.clearbit.com/${companyDomain}`;
    return null;
  };

  const CompanyLogo = ({ name, domain, className }) => {
    const [failed, setFailed] = React.useState(false);
    const src = getCompanyLogo(name, domain);
    const initials = (name || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    if (!src || failed) {
      return (
        <div className={`${className} company-fallback`} style={{
          fontSize: className?.includes('large') ? 20 : 14,
        }}>{initials}</div>
      );
    }
    return <img src={src} alt={name} className={className} onError={() => setFailed(true)} />;
  };

  const renderJobDetail = () => selectedJob && (
          <div className="job-detail">
            <div className="job-detail-header">
              <div className="job-detail-company">
                <CompanyLogo name={selectedJob.company_name} domain={selectedJob.company_logo_domain || selectedJob.company_domain} className="company-logo-large" />
                <h2
                  className="company-link"
                  onClick={() => navigate(`/companies/${slugify(selectedJob.company_name)}`)}
                >{selectedJob.company_name}</h2>
              </div>
              <button className="more-button">⋯</button>
            </div>

            <h1 className="job-detail-title">{selectedJob.title}</h1>

            <div className="job-detail-meta">
              <span>
                {(selectedJob.cities?.[0] || selectedJob.countries?.[0])
                  ? [selectedJob.cities?.[0], selectedJob.countries?.[0]].filter(Boolean).join(', ')
                  : 'Location not specified'}
              </span>
              {selectedJob.posted_at && !isNaN(new Date(selectedJob.posted_at)) && (
                <>
                  <span>•</span>
                  <span>
                    Posted{' '}
                    {Math.max(0, Math.floor(
                      (Date.now() - new Date(selectedJob.posted_at)) / (1000 * 60 * 60 * 24)
                    ))}{' '}
                    days ago
                  </span>
                </>
              )}
              {/* feed v2 drops this claim: the data does not support it */}
              {!feedV2 && <span>•</span>}
              {!feedV2 && <span>Over 100 people clicked apply</span>}
            </div>

            <div className="job-detail-badges">
              {formatSalary(selectedJob) && (
                <span className="badge">{formatSalary(selectedJob)}</span>
              )}
              {selectedJob.remote && <span className="badge badge-remote">✓ Remote</span>}
              {selectedJob.employment_type && (
                <span className="badge">✓ {selectedJob.employment_type.replace('_', '-')}</span>
              )}
            </div>

            <div className="job-detail-actions">
              <button
                className="apply-button"
                disabled={seedingJob === selectedJob.id}
                onClick={async () => {
                  trackFunnel('apply_click', { job_id: Number(selectedJob.id), signed_in: !!session, ext: extensionInstalled });
                  if (!session) { setAuthTab('signin'); setShowAuthModal(true); return; }
                  setSeedingJob(selectedJob.id);
                  const url = new URL(selectedJob.apply_url);
                  url.searchParams.set('sa_autofill', '1');
                  window.open(url.toString(), '_blank', 'noopener,noreferrer');
                  setPendingApply({
                    id: `pending-${Date.now()}`,
                    job_url: selectedJob.apply_url,
                    job_title: selectedJob.title,
                    company: selectedJob.company_name,
                    status: 'opened',
                    created_at: new Date().toISOString(),
                  });
                  markApplied(selectedJob.id);
                  setSeedingJob(null);

                  // Persist the application — without this it only lived in
                  // local React state, so it vanished on refresh, never
                  // counted toward appliedCount, and never got excluded from
                  // future "Recommended for you" results.
                  try {
                    await fetch(`${API_URL}/api/ai-resume/applications/track`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                      body: JSON.stringify({
                        jobUrl: selectedJob.apply_url,
                        jobTitle: selectedJob.title,
                        company: selectedJob.company_name,
                      }),
                    });
                  } catch (e) {
                    console.error('Failed to record application', e);
                  }
                }}
              >
                {seedingJob === selectedJob.id
                  ? 'Opening…'
                  : appliedJobIds.has(selectedJob.id) ? '✓ Applied — Apply again?' : (feedV2 ? 'Apply with autofill' : '⚡ Apply')} <ExternalLink size={16} />
              </button>
            </div>
            {extensionInstalled ? (
              <p className="hint-text">
                ⚡ We'll auto-fill the form for you — just click Submit when ready.
              </p>
            ) : (
              <p className="hint-text">
                ⚡ Auto-fill requires the{' '}
                <a href={EXTENSION_URL} target="_blank" rel="noopener noreferrer">
                  SimplyApply Chrome extension
                </a>
                {' '}— install it once, then click Apply.
              </p>
            )}

            <div className="job-detail-description">
              <h3>About the job</h3>
              {selectedJob.description_md || selectedJob.description_excerpt ? (
                <div
                  className="description-content"
                  dangerouslySetInnerHTML={{ __html: unescapeMarkdown(selectedJob.description_md || selectedJob.description_excerpt) }}
                />
              ) : feedV2 && selectedJob.description_md === undefined ? (
                // feed v2 only: cards carry no excerpt, so undefined means the
                // lazy description_md fetch in selectJob is still in flight
                <p>Loading description…</p>
              ) : (
                <p>No description available.</p>
              )}
            </div>

            {selectedJob.skills && selectedJob.skills.length > 0 && (
              <div className="job-skills">
                <h3>Skills</h3>
                <div className="skills-list">
                  {selectedJob.skills.map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
  );

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="logo"><span className="logo-mark"><Zap size={16} fill="currentColor" /></span>SimplyApply</h1>
          <nav className="nav">
            <a href="#jobs" onClick={e => { e.preventDefault(); setActiveTab('jobs'); }} className={activeTab === 'jobs' ? 'nav-active' : ''}><Briefcase size={20} /><span>Jobs</span></a>
            <a href="#applications" onClick={e => { e.preventDefault(); setActiveTab('applications'); }} className={activeTab === 'applications' ? 'nav-active' : ''}><FileText size={20} /><span>Applications</span></a>
            {session && (
              <a href="#growth" onClick={e => { e.preventDefault(); setActiveTab('growth'); }} className={activeTab === 'growth' ? 'nav-active' : ''}><TrendingUp size={20} /><span>Growth</span></a>
            )}
            {session ? (
              <div className="nav-user">
                {ONBOARDING_ENABLED && (
                  <button className="nav-setup" onClick={() => setShowWizard(true)}>Set up autofill</button>
                )}
                <span className="nav-email">{session.user.email}</span>
                <button
                  className="nav-signout"
                  onClick={() => supabase.auth.signOut()}
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                className="nav-signin"
                onClick={() => { setAuthTab('signin'); setShowAuthModal(true); }}
              >
                Sign In
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero — logged-out visitors only. There was previously no stated value prop at
          all above the fold for a first-time visitor (straight into a bare job list),
          which is a likely factor in the near-total post-signup drop-off. Stats are
          hardcoded from a 2026-08-17 DB snapshot — refresh periodically rather than
          wiring a live count endpoint, since this is a low-traffic marketing surface. */}
      {!feedV2 && !session && (
        <div className="hero-card">
          <div className="hero-copy">
            <h2>309,000+ Real Jobs. One-Click Apply.</h2>
            <p>
              Real openings sourced straight from 11,000+ companies' own career pages — then autofill the application on Greenhouse, Lever, Ashby, Workday, and more. Free.
            </p>
            <a className="hero-cta" href={EXTENSION_URL} target="_blank" rel="noopener noreferrer">
              Add to Chrome — Free
            </a>
          </div>
          <div className="hero-stats">
            <div className="hero-stat"><b>309K+</b><span>Jobs</span></div>
            <div className="hero-stat"><b>11,404</b><span>Companies</span></div>
            <div className="hero-stat"><b>37K+</b><span>Remote roles</span></div>
          </div>
        </div>
      )}

      {/* Value-first hero for logged-out visitors on the v2 feed: promise, sign-up button and a
          looped autofill demo. Waits for the session check unless no stored session exists, so a
          returning signed-in visitor never sees it flash in. */}
      {ONBOARDING_ENABLED && feedV2 && !session && activeTab === 'jobs' && (sessionChecked || !storedSessionAtLoad) && (
        <ValueHero onGetStarted={() => { setAuthTab('signup'); setShowAuthModal(true); }} />
      )}

      {/* Extension install banner — signed-in users only */}
      {session && (
        <div className="ext-banner">
          <span>
            ⚡ Autofill any job application in one click with the SimplyApply Chrome extension
          </span>
          <a className="ext-banner-btn" href={EXTENSION_URL} target="_blank" rel="noopener noreferrer">
            Add to Chrome — Free
          </a>
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="legacy-light">
          <ApplicationsTab
            session={session}
            API_URL={API_URL}
            EXTENSION_URL={EXTENSION_URL}
            pendingApply={pendingApply}
            onPendingConsumed={() => setPendingApply(null)}
          />
        </div>
      )}

      {/* Growth Tab */}
      {activeTab === 'growth' && session && (
        <div className="legacy-light">
          <GrowthTab
            session={session}
            API_URL={API_URL}
            onSearchTerm={searchJobs}
            onCompanyClick={company => navigate(`/companies/${slugify(company)}`)}
          />
        </div>
      )}

      {/* Search Bar */}
      {activeTab === 'jobs' && !feedV2 && <>
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search jobs, titles, companies..."
              value={filters.keyword}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
              className="search-input"
            />
          </div>
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filters">
          {[
            { label: 'Remote', key: 'remote', toggle: true },
            { label: 'Full-time', key: 'employmentType', value: 'full_time' },
            { label: 'Part-time', key: 'employmentType', value: 'part_time' },
            { label: 'Contract', key: 'employmentType', value: 'contract' },
            { label: 'Past week', key: 'datePosted', value: '7' },
            { label: 'Past month', key: 'datePosted', value: '30' },
            { label: 'LinkedIn', key: 'provider', value: 'linkedin' },
          ].map(f => {
            const active = f.toggle ? filters.remote : filters[f.key] === f.value;
            return (
              <button
                key={f.label}
                className={`filter-pill${active ? ' active' : ''}`}
                onClick={() => {
                  if (f.toggle) {
                    setFilters({ ...filters, remote: !filters.remote });
                  } else {
                    setFilters({ ...filters, [f.key]: active ? '' : f.value });
                  }
                }}
              >
                {f.label} {active ? '✕' : '+'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Extension CTA — not signed in */}
      {!session && (
        <div className="ext-banner">
          <span>Find a job you like?{' '}
          <a
            href={EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Install the SimplyApply extension
          </a>
          {' '}to autofill the application in one click.</span>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Job List */}
        <div className="job-list">
          <div className="job-list-header">
            <h2>{session && !filters.keyword && !filters.location && !filters.remote ? 'Recommended for you' : 'Top job picks for you'}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <p className="results-count">{totalCount} results</p>
              {session && appliedCount > 0 && (
                <button
                  onClick={() => setShowApplied(v => !v)}
                  className={`toggle-applied${showApplied ? ' on' : ''}`}
                >
                  {showApplied ? `✓ Showing applied (${appliedCount})` : `Hide applied (${appliedCount})`}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="no-results">
              <p>No jobs found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="jobs">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className={`job-card ${selectedJob?.id === job.id ? 'selected' : ''}`}
                  onClick={() => selectJob(job)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="job-card-header">
                    <CompanyLogo name={job.company_name} domain={job.company_logo_domain || job.company_domain} className="company-logo" />
                    <div className="job-card-title">
                      <h3>{job.title}</h3>
                      <p className="company-name">{job.company_name}</p>
                      {appliedJobIds.has(job.id) && (
                        <span className="applied-chip">✓ Applied</span>
                      )}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button
                        className="close-button"
                        onClick={e => {
                          e.stopPropagation();
                          if (dismissMenuJobId === job.id) { setDismissMenuJobId(null); return; }
                          const r = e.currentTarget.getBoundingClientRect();
                          setDismissMenuPos({ top: r.bottom + 4, left: r.right });
                          setDismissMenuJobId(job.id);
                        }}
                      >×</button>
                      {dismissMenuJobId === job.id && dismissMenuPos && createPortal(
                        <div
                          className="dismiss-menu"
                          onClick={e => e.stopPropagation()}
                          style={{
                            position: 'fixed', top: dismissMenuPos.top, left: dismissMenuPos.left,
                            transform: 'translateX(-100%)', zIndex: 1000,
                          }}
                        >
                          <button onClick={() => dismissJob(job, 'company')}>Don't show jobs from {job.company_name}</button>
                          <button onClick={() => dismissJob(job, 'title')}>Don't show "{job.title}" roles</button>
                          {(job.cities?.length > 0 || job.countries?.length > 0) && (
                            <button onClick={() => dismissJob(job, 'location')}>Don't show jobs from {job.cities?.[0] || job.countries?.[0]}</button>
                          )}
                          <button onClick={() => dismissJob(job, 'card')}>Just remove this card</button>
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>

                  <div className="job-card-info">
                    <div className="job-location">
                      <MapPin size={14} />
                      <span>
                        {(job.cities?.[0] || job.countries?.[0])
                          ? [job.cities?.[0], job.countries?.[0]].filter(Boolean).join(', ')
                          : 'Remote'}
                        {job.remote && ' (Remote)'}
                      </span>
                    </div>
                    {formatSalary(job) && (
                      <div className="job-salary">
                        <DollarSign size={14} />
                        <span>{formatSalary(job)}</span>
                      </div>
                    )}
                  </div>

                  <div className="job-card-footer">
                    <span className="job-time">
                      <Clock size={14} />
                      {new Date(job.posted_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>

                  </div>
                </div>
              ))}

              {/* Pagination */}
              {!loading && (page > 0 || totalCount === PAGE_SIZE) && (
                <div className="pagination">
                  <button className="page-btn prev-next" disabled={page === 0}
                    onClick={() => { const p = page - 1; setPage(p); fetchJobs(p); }}>←</button>
                  {[...Array(Math.min(5, page + (totalCount === PAGE_SIZE ? 2 : 1)))].map((_, i) => {
                    const start = Math.max(0, page - 2);
                    const p = start + i;
                    if (p > page && totalCount < PAGE_SIZE) return null;
                    return (
                      <button key={p} className={`page-btn${p === page ? ' active' : ''}`}
                        onClick={() => { if (p !== page) { setPage(p); fetchJobs(p); } }}>
                        {p + 1}
                      </button>
                    );
                  })}
                  <button className="page-btn prev-next" disabled={totalCount < PAGE_SIZE}
                    onClick={() => { const p = page + 1; setPage(p); fetchJobs(p); }}>→</button>
                </div>
              )}
            </div>
          )}
        </div>

        {renderJobDetail()}
      </div>
      </>}
      {activeTab === 'jobs' && feedV2 && (
        <FeedPage
          apiBase={API_URL}
          session={session}
          selectedJob={selectedJob}
          onSelectJob={selectJob}
          detail={renderJobDetail()}
          extensionUrl={EXTENSION_URL}
          preload={window.__feedPreload}
          externalQuery={feedQuery}
          appliedJobIds={appliedJobIds}
        />
      )}
      {showAuthModal && (
        <AuthModal
          initialTab={authTab}
          onSuccess={(s, isNewUser) => {
            setSession(s);
            if (isNewUser) {
              try { localStorage.setItem('sa_signup_tracked', '1'); } catch {}
              trackFunnel('signup_complete', { method: 'password' });
            }
          }}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      {showWizard && session && (
        <Suspense fallback={null}>
          <OnboardingWizard
            session={session}
            onClose={(why) => {
              setShowWizard(false);
              if (why === 'jobs') {
                setActiveTab('jobs');
                setTimeout(() => { const el = document.querySelector('.feed-page'); if (el && el.scrollIntoView) el.scrollIntoView(); }, 0);
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;